// src/presentation/pages/ClinicalExamination/types.ts
export interface SensoryFormData {
  visionStatus: string;
  visionAssistive: {
    wearsGlasses: boolean;
    usesContacts: boolean;
    sensitiveToLight: boolean;
  };
  visualAidPurpose: {
    forReading: boolean;
    forDistance: boolean;
    other: boolean;
  };
  visualAidOtherText: string;
  hearingStatus: string;
  hearingAssistive: {
    usesHearingAid: boolean;
    hasCochlearImplant: boolean;
    usesSignLanguage: boolean;
  };
  speechStatus: string;
  communicationMethods: {
    speaksIndependently: boolean;
    usesAACDevice: boolean;
    usesGestures: boolean;
  };
}

// src/presentation/pages/ClinicalExamination/types.ts

export interface NeurologicalHistoryFormData {
  hasNeurologicalDiagnosis: boolean | null;
  neurologicalCondition: string;
  neurologicalDiagnosisYear: string;
  hasHadStroke: boolean | null;
  strokeYear: string;
  hasHadTIA: boolean | null;
  tiaYear: string;
  hasMultipleStrokes: boolean | null;
  multipleStrokesYear: string;
  hasHeadInjury: boolean | null;
  headInjuryLossOfConsciousness: boolean | null;
  headInjuryYear: string;
  hasHypertension: boolean | null;
  hypertensionYear: string;
  hasDiabetes: boolean | null;
  diabetesYear: string;
  hasHighCholesterol: boolean | null;
  highCholesterolYear: string;
  hasHeartDisease: boolean | null;
  heartDiseaseYear: string;
  hasFamilyNeurodegenerative: boolean | null;
  familyRelation: string;
  familyOtherRelation: string;
  onsetPattern: "gradual" | "sudden" | "stepwise" | null;
}

export const initialNeurologicalFormData: NeurologicalHistoryFormData = {
  hasNeurologicalDiagnosis: false,
  neurologicalCondition: "",
  neurologicalDiagnosisYear: "",
  hasHadStroke: false,
  strokeYear: "",
  hasHadTIA: false,
  tiaYear: "",
  hasMultipleStrokes: false,
  multipleStrokesYear: "",
  hasHeadInjury: false,
  headInjuryLossOfConsciousness: false,
  headInjuryYear: "",
  hasHypertension: false,
  hypertensionYear: "",
  hasDiabetes: false,
  diabetesYear: "",
  hasHighCholesterol: false,
  highCholesterolYear: "",
  hasHeartDisease: false,
  heartDiseaseYear: "",
  hasFamilyNeurodegenerative: false,
  familyRelation: "",
  familyOtherRelation: "",
  onsetPattern: "gradual",
};

// Add BehavioralFormData interface
export interface BehavioralFormData {
  sleepQuality: string;
  depressionFrequency: string;
  anxietyLevel: string;
  apathyLevel: string;
}

export const initialSensoryFormData: SensoryFormData = {
  visionStatus: "normal",
  visionAssistive: {
    wearsGlasses: false,
    usesContacts: false,
    sensitiveToLight: false,
  },
  visualAidPurpose: {
    forReading: false,
    forDistance: false,
    other: false,
  },
  visualAidOtherText: "",
  hearingStatus: "normal",
  hearingAssistive: {
    usesHearingAid: false,
    hasCochlearImplant: false,
    usesSignLanguage: false,
  },
  speechStatus: "clear",
  communicationMethods: {
    speaksIndependently: false,
    usesAACDevice: false,
    usesGestures: false,
  },
};

// Add initial behavioral form data
export const initialBehavioralFormData: BehavioralFormData = {
  sleepQuality: "excellent",
  depressionFrequency: "not-at-all",
  anxietyLevel: "not-anxious",
  apathyLevel: "no-apathy",
};

export interface CognitiveFunctionalFormData {
  // Memory
  forgetsRecentEvents: boolean | null;
  repeatsQuestions: boolean | null;

  // Executive Function
  difficultyPlanning: boolean | null;
  difficultyManagingFinances: boolean | null;

  // Language
  difficultyFindingWords: boolean | null;
  difficultyUnderstandingSpeech: boolean | null;

  // Visuospatial
  getsLostFamiliarPlaces: boolean | null;
  difficultyRecognizingFaces: boolean | null;

  // Perception & Sleep Behavior
  visualHallucinations: boolean | null;
  actingOutDreams: boolean | null;

  // Motor Symptoms
  slownessOrStiffness: boolean | null;
  slowMovementOrBalanceIssues: boolean | null;
}

export const booleanRadioValue = (
  value: boolean | null | undefined,
): string => (value === null || value === undefined ? "" : String(value));

export const isCognitiveFormEmpty = (
  cognitive: CognitiveFunctionalFormData,
): boolean =>
  (
    Object.keys(cognitive) as Array<keyof CognitiveFunctionalFormData>
  ).every((key) => cognitive[key] === null || cognitive[key] === undefined);

export const initialCognitiveFunctionalFormData: CognitiveFunctionalFormData = {
  forgetsRecentEvents: false,
  repeatsQuestions: false,
  difficultyPlanning: false,
  difficultyManagingFinances: false,
  difficultyFindingWords: false,
  difficultyUnderstandingSpeech: false,
  getsLostFamiliarPlaces: false,
  difficultyRecognizingFaces: false,
  visualHallucinations: false,
  actingOutDreams: false,
  slownessOrStiffness: false,
  slowMovementOrBalanceIssues: false,
};

export interface ScreeningFormData {
  isScreeningRequired: boolean | null;
  selectedDoctors: string[];
}

export const initialScreeningFormData: ScreeningFormData = {
  isScreeningRequired: true, // Default to Yes as shown in image
  selectedDoctors: [],
};
